import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { getAttendance, setAttendance, RECENTS_MAX } from '../sdk/saleSelectionStorage.js';
import { biddingStore } from './bidding.store.js';

/**
 * Which sale am I in.
 *
 * It owns the selected sale and the recent-sales quick-switch, and remembers
 * both on the device through the storage port. That device-scoped half is what
 * made this look like a mobile store; it is not. "Which sale am I in" is a
 * shared concept — a desktop surface attending a sale is attending the same
 * sale, and would otherwise have written this again — and the device-scoped
 * part is a port rather than a platform.
 *
 * What did NOT come with it is the three members that read the SURFACE's
 * filtered tree: `scopedGroups`, `hasScopedLots` and `selectedSaleTitle` narrow
 * `visibleGroups` to the attended sale, and `visibleGroups` is one screen's
 * answer to having no room for the whole block. They recompute from
 * `selectedSale` on whichever surface is drawing.
 *
 * It imports biddingStore one-way — biddingStore never imports this — so the
 * global grouping and the notification path stay decoupled.
 */

export const attendanceStore = defineStore('attendanceStore', () => {
  const bidding = biddingStore();

  const selectedSale = ref(null);
  const recentSales = ref([]);
  const hydrated = ref(false);

  function pushRecent(sale) {
    recentSales.value = [sale, ...recentSales.value.filter((s) => s !== sale)].slice(0, RECENTS_MAX);
  }

  // The single entry point for "attend this sale". Auto-select routes through
  // it too, so the active sale is always in recents and therefore the switcher.
  function selectSale(sale) {
    if (!sale) return;
    selectedSale.value = sale;
    pushRecent(sale);
  }

  // Reconcile the selection against the loaded sales: prune what has ended. No-op until
  // hydrated so a persisted value isn't stomped before storage is read.
  function validateSelection() {
    if (!hydrated.value) return;
    const names = bidding.saleNames;
    if (!names.length) return; // lots not loaded yet — keep selection as-is
    // Prune any sale that has ended from recents.
    if (recentSales.value.some((s) => !names.includes(s))) {
      recentSales.value = recentSales.value.filter((s) => names.includes(s));
    }
    // Current selection still valid → keep it.
    if (selectedSale.value && names.includes(selectedSale.value)) return;
    // Otherwise nothing is attended. A sale is never picked for the dealer: a stored one that
    // has ended, or none ever chosen, leaves the Live tab to send them to choose one.
    selectedSale.value = null;
  }

  // Read persisted selection once, then validate. Called when the Sales tab
  // mounts, and before the Dashboard attends a sale.
  async function hydrate() {
    if (hydrated.value) return;
    const stored = await getAttendance();
    selectedSale.value = stored.selectedSale;
    recentSales.value = stored.recentSales;
    hydrated.value = true;
    validateSelection();
  }

  // Re-validate whenever the loaded sales change (first load, demo reset, a sale
  // ending). hydrate() drives the first pass, so no immediate here.
  watch(() => bidding.saleNames, validateSelection);

  // Persist selection + recents. Best-effort; never blocks a switch.
  watch([selectedSale, recentSales], () => {
    if (!hydrated.value) return;
    void setAttendance({ selectedSale: selectedSale.value, recentSales: recentSales.value });
  });

  return {
    selectedSale,
    recentSales,
    hydrated,
    selectSale,
    hydrate,
    validateSelection
  };
});
